import {
	Button,
	Checkbox,
	HStack,
	Input,
	VStack,
} from '@montel/montelpro-shared-components-v2';
import { COMPANIES_UPDATE_COMPANY } from 'apiRoutes';
import CountrySelect from 'components/common/CountrySelect';
import { MultiInput } from 'components/common/MultiInput';
import {
	validateCompanyName,
	validateCountryCode,
	validateDomain,
	validateHubspotId,
	validateYouniumId,
} from 'components/companies/companyValidation';
import { Company } from 'domain/company/Company';
import useSubmit, { POST } from 'hooks/useSubmit';
import { FunctionComponent, useState } from 'react';
import { styled } from 'styled-components';
import { RequestStatus } from 'types/RequestStatus';
import { toDefaultDateFormat } from 'utils/dateUtils';

const EditableCompanyDetails: FunctionComponent<{
	company: Company;
	setIsEditing: (isEditing: boolean) => void;
	onSuccessfulSubmit: () => Promise<void>;
}> = ({ company, setIsEditing, onSuccessfulSubmit }) => {
	const [formState, setFormState] = useState<{
		companyId: number;
		name: string;
		countryCode: string;
		emailDomains: string[];
		youniumId: string;
		hubspotId: string;
		youniumAccountNumber: string;
		isCustomer: boolean;
	}>({
		companyId: company.id,
		name: company.name,
		countryCode: company.country?.twoLetterIsoCode,
		emailDomains: company.emailDomains ?? [],
		youniumId: company.youniumId ?? '',
		hubspotId: company.hubspotId ?? '',
		youniumAccountNumber: company.youniumAccountNumber ?? '',
		isCustomer: company.isCustomer ?? false,
	});

	const [errors, setErrors] = useState<{
		name?: string;
		country?: string;
		youniumId?: string;
		hubspotId?: string;
		emailDomains?: string;
		youniumAccountNumber?: string;
	}>({
		name: undefined,
		country: undefined,
		youniumId: undefined,
		hubspotId: undefined,
		emailDomains: undefined,
		youniumAccountNumber: undefined,
	});

	const [updateCompany, updateCompanyStatus] = useSubmit(
		COMPANIES_UPDATE_COMPANY,
		POST,
		{
			messageOnSuccess: 'Company info successfully updated.',
			messageOnError: 'An error occured, please try again.',
			showApiError: true,
		},
	);

	const isValid = !Boolean(
		errors.name ||
			formState.name.length < 3 ||
			errors.country ||
			errors.emailDomains ||
			errors.youniumId ||
			errors.hubspotId ||
			errors.youniumAccountNumber,
	);
	const hasValues = Boolean(formState.name && formState.countryCode);

	return (
		<VStack gap="xl">
			<HStack
				fullWidth
				align="center"
				justify="space-between"
				wrap="nowrap"
			>
				<HStack align="center">
					<StyledHeaderInput
						value={formState.name}
						size="md"
						label="Name"
						onChange={(event) => {
							setFormState({
								...formState,
								name: event.target.value,
							});

							setErrors({
								...errors,
								name: validateCompanyName(event.target.value),
							});
						}}
						errorMessage={errors.name}
					/>
					<Checkbox
						label="This company is a customer"
						size="md"
						checked={formState.isCustomer}
						onChange={(e) =>
							setFormState({
								...formState,
								isCustomer: e.target.checked,
							})
						}
					/>
				</HStack>
				<HStack wrap="nowrap">
					<Button
						text="Cancel"
						variant="secondary"
						onClick={() => setIsEditing(false)}
					/>
					<Button
						text="Submit"
						isLoading={
							updateCompanyStatus === RequestStatus.LOADING
						}
						onClick={async () => {
							await updateCompany({
								companyId: formState.companyId,
								name: formState.name,
								countryCode: formState.countryCode,
								emailDomains: formState.emailDomains,
								youniumId: formState.youniumId || null,
								hubspotId: formState.hubspotId || null,
								youniumAccountNumber:
									formState.youniumAccountNumber || null,
								isCustomer: formState.isCustomer,
							});
							onSuccessfulSubmit();
							setIsEditing(false);
						}}
						isDisabled={
							!isValid ||
							!hasValues ||
							updateCompanyStatus === RequestStatus.LOADING
						}
					/>
				</HStack>
			</HStack>
			<HStack wrap="nowrap" gap="xl" fullWidth>
				<StyledInput
					value={formState.youniumId}
					size="sm"
					label="Younium ID"
					errorMessage={errors.youniumId}
					onChange={(event) => {
						setErrors({
							...errors,
							youniumId: undefined,
						});

						setFormState({
							...formState,
							youniumId: event.target.value,
						});
					}}
					onBlur={() =>
						setErrors({
							...errors,
							youniumId: validateYouniumId(formState.youniumId),
						})
					}
				/>
				<StyledInput
					value={formState.youniumAccountNumber}
					size="sm"
					label="Younium Account Number"
					errorMessage={errors.youniumAccountNumber}
					onChange={(event) => {
						setErrors({
							...errors,
							youniumAccountNumber: undefined,
						});

						setFormState({
							...formState,
							youniumAccountNumber: event.target.value,
						});
					}}
				/>
				<StyledInput
					value={formState.hubspotId}
					size="sm"
					label="Hubspot ID"
					errorMessage={errors.hubspotId}
					onChange={(event) => {
						setErrors({
							...errors,
							hubspotId: undefined,
						});

						setFormState({
							...formState,
							hubspotId: event.target.value,
						});
					}}
					onBlur={() =>
						setErrors({
							...errors,
							hubspotId: validateHubspotId(formState.hubspotId),
						})
					}
				/>
			</HStack>
			<HStack wrap="nowrap" gap="xl" fullWidth>
				<StyledCountrySelect
					value={formState.countryCode}
					onChange={function (countryCode: string): void {
						setErrors({
							...errors,
							country: undefined,
						});

						setFormState({
							...formState,
							countryCode: countryCode,
						});
					}}
					onBlur={() => validateCountryCode(formState.countryCode)}
				/>
				<StyledMultiInput
					label="Email domains"
					inputSize="sm"
					labelSize="xs"
					values={formState.emailDomains}
					setValue={(value) => {
						setFormState({
							...formState,
							emailDomains: value,
						});
					}}
					validateFn={validateDomain}
					error={errors.emailDomains}
					setError={(error) =>
						setErrors({
							...errors,
							emailDomains: error,
						})
					}
				/>
				<StyledInput
					disabled
					size="sm"
					label="Created (UTC)"
					value={
						company.created
							? toDefaultDateFormat(company.created)
							: ''
					}
				/>
			</HStack>
		</VStack>
	);
};

export default EditableCompanyDetails;

const StyledHeaderInput = styled(Input)`
	width: 100%;
`;

const StyledInput = styled(Input)`
	width: 30%;
`;

const StyledMultiInput = styled(MultiInput)`
	width: 30%;
`;

const StyledCountrySelect = styled(CountrySelect)`
	width: 30%;
`;
